import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, RefreshCw, Loader2, Send, Users, Users2, User, Search, Check } from "lucide-react";
import { API } from "@/lib/api";


const TARGETS = [
  { key: "all", label: "All customers", icon: Users },
  { key: "group", label: "A group", icon: Users2 },
  { key: "customers", label: "Specific customers", icon: User },
];

export default function VendorNudgePage() {
  const { authHeaders, user } = useAuth();
  const vid = user?.vendor_id;
  const [msgTitle, setMsgTitle] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [msgResult, setMsgResult] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  // Targeting
  const [target, setTarget] = useState("all");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [custSearch, setCustSearch] = useState("");

  const loadTargets = useCallback(async () => {
    if (!vid) return;
    try {
      const [g, c] = await Promise.all([
        axios.get(`${API}/customer-groups/${vid}`, { headers: authHeaders() }),
        axios.get(`${API}/customers?client_id=${vid}&limit=100`, { headers: authHeaders() }),
      ]);
      setGroups(g.data || []);
      setCustomers(c.data.customers || []);
    } catch (err) { console.error(err); }
  }, [vid, authHeaders]);

  useEffect(() => { loadTargets(); }, [loadTargets]);

  const recipientCount = target === "all"
    ? null
    : target === "group"
      ? (groups.find(g => g.id === selectedGroup)?.member_count ?? 0)
      : selectedCustomers.length;

  const canSend = msgTitle.trim() && msgBody.trim() && !msgSending && (
    target === "all" ||
    (target === "group" && selectedGroup) ||
    (target === "customers" && selectedCustomers.length > 0)
  );

  const toggleCustomer = (id) => setSelectedCustomers(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setMsgSending(true);
    setMsgResult(null);
    const payload = { title: msgTitle, body: msgBody, target };
    if (target === "group") payload.group_id = selectedGroup;
    if (target === "customers") payload.customer_ids = selectedCustomers;
    try {
      const { data } = await axios.post(`${API}/push/send/${vid}`, payload, { headers: authHeaders() });
      setMsgResult(data);
      setMsgTitle("");
      setMsgBody("");
    } catch (err) {
      setMsgResult({ error: err.response?.data?.detail || "Failed to send" });
    } finally { setMsgSending(false); }
  };

  const handlePushAll = async () => {
    setPushLoading(true);
    setPushResult(null);
    try {
      const { data } = await axios.post(`${API}/push/send/${vid}`, { title: "Pass Updated", body: "Your loyalty pass has been updated.", target: "all" }, { headers: authHeaders() });
      setPushResult(data);
    } catch (err) {
      setPushResult({ error: err.response?.data?.detail || "Failed" });
    } finally { setPushLoading(false); }
  };

  const filteredCustomers = customers.filter(c => {
    const q = custSearch.toLowerCase();
    return !q || (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.mobile_number || "").includes(q);
  });

  return (
    <div className="space-y-5 max-w-2xl" data-testid="vendor-nudge-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Nudge</h1>
        <p className="text-sm text-ink/40 mt-0.5">Send push notifications to everyone, a group, or specific customers</p>
      </div>

      <Card className="bg-white border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2 pt-5 px-5">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Send size={14} className="text-ink/50" /> Send Notification
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          {/* Target selector */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-ink/50">Send to</p>
            <div className="grid grid-cols-3 gap-2">
              {TARGETS.map(t => {
                const Icon = t.icon;
                const active = target === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => setTarget(t.key)}
                    data-testid={`target-${t.key}`}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-colors ${active ? "bg-purple-500 text-white border-ink" : "bg-white text-ink/60 border-ink/15 hover:border-ink/30"}`}>
                    <Icon size={16} />
                    <span className="text-[11px] font-medium">{t.label}</span>
                  </button>
                );
              })}
            </div>

            {target === "group" && (
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger className="h-10 rounded-xl mt-1" data-testid="nudge-group-select"><SelectValue placeholder="Choose a group" /></SelectTrigger>
                <SelectContent>
                  {groups.length === 0
                    ? <div className="px-3 py-2 text-xs text-ink/40">No groups yet — create one in Customers › Groups</div>
                    : groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name} ({g.member_count})</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {target === "customers" && (
              <div className="mt-1 border border-ink/15 rounded-xl overflow-hidden">
                <div className="relative border-b border-ink/10">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/30" />
                  <Input value={custSearch} onChange={e => setCustSearch(e.target.value)} placeholder="Search customers" className="h-9 pl-9 border-0 rounded-none text-sm focus-visible:ring-0" data-testid="nudge-customer-search" />
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {filteredCustomers.length === 0 ? (
                    <p className="text-xs text-ink/40 text-center py-6">No customers found.</p>
                  ) : filteredCustomers.map(c => {
                    const on = selectedCustomers.includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => toggleCustomer(c.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${on ? "bg-purple-100" : "hover:bg-surface"}`}
                        data-testid={`nudge-pick-${c.id}`}>
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-purple-500 border-ink" : "border-ink/30"}`}>
                          {on && <Check size={11} className="text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-ink truncate">{c.name || "Unknown"}</p>
                          <p className="text-[11px] text-ink/40 truncate">{c.email || c.mobile_number || "—"}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {recipientCount !== null && (
              <p className="text-[11px] text-ink/50" data-testid="recipient-count">
                {recipientCount} recipient{recipientCount !== 1 ? "s" : ""} selected
              </p>
            )}
          </div>

          <p className="text-xs text-ink/50">Reaches Apple Wallet users (pass refresh) and web push subscribers. The title appears as the main notification text on Apple Wallet.</p>
          <form onSubmit={handleSendMessage} className="space-y-3">
            <Input
              placeholder="Notification title, e.g. Double Stamps Weekend!"
              value={msgTitle}
              onChange={(e) => setMsgTitle(e.target.value)}
              className="h-10 rounded-xl"
              data-testid="push-msg-title"
              required
            />
            <Input
              placeholder="Message body, e.g. Earn 2x stamps on all orders this Saturday..."
              value={msgBody}
              onChange={(e) => setMsgBody(e.target.value)}
              className="h-10 rounded-xl"
              data-testid="push-msg-body"
              required
            />
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={!canSend} data-testid="send-message-btn" className="gap-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-full">
                {msgSending ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
                {msgSending ? "Sending..." : "Send Notification"}
              </Button>
              {msgResult && !msgResult.error && (
                <span className="text-xs text-ink" data-testid="msg-result">
                  Sent to {msgResult.sent} device(s)
                  {msgResult.recipients != null && <span className="text-ink/40"> · {msgResult.recipients} recipient(s)</span>}
                  {(msgResult.apns?.sent > 0 || msgResult.fcm?.sent > 0 || msgResult.google?.updated > 0) && (
                    <span className="text-ink/40">
                      {" "}({msgResult.apns?.sent || 0} Apple, {msgResult.fcm?.sent || 0} Web, {msgResult.google?.updated || 0} Google)
                    </span>
                  )}
                </span>
              )}
              {msgResult?.error && <span className="text-xs text-danger" data-testid="msg-error">{msgResult.error}</span>}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2 pt-5 px-5">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <RefreshCw size={14} className="text-ink/50" /> Apple Wallet Refresh
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-3">
          <p className="text-xs text-ink/50">Force-refresh all Apple Wallet passes. Passes auto-refresh when stamps/rewards change.</p>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handlePushAll} disabled={pushLoading} data-testid="push-all-btn" className="gap-1.5 rounded-full">
              {pushLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {pushLoading ? "Sending..." : "Refresh All Passes"}
            </Button>
            {pushResult && !pushResult.error && (
              <span className="text-xs text-ink/60" data-testid="push-result">
                <span className="font-semibold text-ink">{pushResult.sent}</span> sent / <span className="font-semibold">{pushResult.total_devices}</span> devices
              </span>
            )}
            {pushResult?.error && <span className="text-xs text-danger" data-testid="push-error">{pushResult.error}</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
